import { LeapYear, SurchargeUnit } from '../constants'
import {
  DynamicTariffSettingsDto,
  GridFeesSettingsDto,
  GridFeeState,
  ManualPowerPricesDto,
  PowerPricesSettingsDto,
  PowerPriceState,
  VariableGridFeesDto,
} from '../dto'
import { DynamicTariffSettingsModel, GridFeesSettingsModel, ManualPowerPricesModel, PowerPricesSettingsModel, VariableGridFeesModel } from '../model'
import { ALL_DAYS, dayToIndex, Days } from '@e3dc-react/shell/Libs/date/types'

/* surchargePercent from backend is between 0-1. user see them in frontend between 0-100 */
export const convertSurchargePercentFromDtoToModel = (surchargePercent: string): number => {
  return Number((Number(surchargePercent) * 100).toFixed(2))
}

/* vatPercent from backend is between 0-1. user see them in frontend between 0-100 */
export const convertVatPercentFromDtoToModel = (vatPercent: string): number => {
  return Number((Number(vatPercent) * 100).toFixed(2))
}

/**
 * create date suitable for the dynamic tariff date picker component.
 * only date and month in backend date string are real user values.
 * rest are fake placeholder values.
 * Example
 * input:  "2025-11-21T23:53:12.345Z"
 * output: "1972-11-21T00:00:00.000Z"
 */
export const convertToValidDate = (dateString: string): Date => {
  const date = new Date(dateString)
  const month = date.getUTCMonth()
  const day = date.getUTCDate()
  /* set date to format expected by backend: 1972-{month}-{day}T00:00:00.000Z */
  date.setUTCFullYear(LeapYear, month, day)
  date.setUTCHours(0, 0, 0, 0)
  return date
}

/**
 * create time suitable for the dynamic tariff time picker component
 * only hour and minute in backend date string are real user values.
 * rest are fake placeholder values
 * Example
 * input:  "2025-01-01T00:00:00.000Z"
 * output: "1972-01-01T23:00:00.000Z" (if user is in UTC+1)
 */
export const convertToValidTime = (dateString: string): Date => {
  const date = new Date(dateString)
  /* add timezone offset */
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
  const hour = date.getUTCHours()
  const minute = date.getUTCMinutes()
  /* set time to format expected by backend: 1972-01-01T{hour}:{minute}:00.000Z. note- month is 0 indexed */
  date.setUTCFullYear(LeapYear, 0, 1)
  date.setUTCHours(hour, minute, 0, 0)
  return date
}

const convertWeekdaysDtoToModel = (weekdays: number[]): Days => {
  return ALL_DAYS.reduce((acc, day) => {
    acc[day] = weekdays.includes(dayToIndex[day])
    return acc
  }, {} as Days)
}

const convertManualPowerPricesDtoToModel = (powerPrices: ManualPowerPricesDto[]): ManualPowerPricesModel[] => {
  return powerPrices.map(powerPrice => ({
    weekdays: convertWeekdaysDtoToModel(powerPrice.weekdays),
    from: convertToValidTime(powerPrice.from),
    to: convertToValidTime(powerPrice.to),
    price: powerPrice.price,
    description: powerPrice.description ?? '',
  }))
}

const convertVariableGridFeesDtoToModel = (gridFees: VariableGridFeesDto[]): VariableGridFeesModel[] => {
  return gridFees.map(gridFee => ({
    from: convertToValidDate(gridFee.from),
    to: convertToValidDate(gridFee.to),
    timeFrames: gridFee.timeFrames.map(timeFrame => ({
      from: convertToValidTime(timeFrame.from),
      to: convertToValidTime(timeFrame.to),
      price: Number(timeFrame.price),
      description: timeFrame.description,
    })),
  }))
}

const convertGridFeesSettingsDtoToModel = (gridFeesSettings: GridFeesSettingsDto): GridFeesSettingsModel => {
  switch (gridFeesSettings.gridFeeState) {
    case GridFeeState.Variable:
      return { gridFeeState: GridFeeState.Variable, gridFees: convertVariableGridFeesDtoToModel(gridFeesSettings.gridFees) }
    case GridFeeState.Static:
      return { gridFeeState: GridFeeState.Static, gridFees: Number(gridFeesSettings.gridFees) }
    case GridFeeState.Included:
      return { gridFeeState: GridFeeState.Included, gridFees: null }
  }
}

const convertPowerPricesSettingsDtoToModel = (values: PowerPricesSettingsDto): PowerPricesSettingsModel => {
  switch (values.powerPriceState) {
    case PowerPriceState.Manual:
      return {
        powerPriceState: PowerPriceState.Manual,
        powerPrices: convertManualPowerPricesDtoToModel(values.powerPrices),
        vatPercent: convertVatPercentFromDtoToModel(values.vatPercent),
      }
    case PowerPriceState.Static:
      return {
        powerPriceState: PowerPriceState.Static,
        powerPrices: Number(values.powerPrices),
        vatPercent: convertVatPercentFromDtoToModel(values.vatPercent),
      }
    case PowerPriceState.Dynamic: {
      const hasSurchargeValue = values.surchargeValue !== null && values.surchargeValue !== undefined
      const surchargeUnit = hasSurchargeValue ? SurchargeUnit.CtPerKwh : SurchargeUnit.Percent
      const hasSurchargePercent = values.surchargePercent !== null && values.surchargePercent !== undefined
      const surchargePercent = hasSurchargePercent ? convertSurchargePercentFromDtoToModel(values.surchargePercent as string) : null
      const surchargeValue = hasSurchargeValue ? Number(values.surchargeValue) : null
      return {
        powerPriceState: PowerPriceState.Dynamic,
        taxes: Number(values.taxes),
        vatPercent: convertVatPercentFromDtoToModel(values.vatPercent),
        surchargePercent,
        surchargeValue,
        surchargeUnit,
      }
    }
  }
}

/**
 * Transform dynamic tariff settings api data to useable format for frontend
 */
export const dynamicTariffSettingsDtoToModelTransformer = (values: DynamicTariffSettingsDto): DynamicTariffSettingsModel => {
  const powerPriceSettings = convertPowerPricesSettingsDtoToModel(values)
  const gridFeesSettings = convertGridFeesSettingsDtoToModel(values)

  return {
    ...gridFeesSettings,
    ...powerPriceSettings,
  }
}
